import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { IdCard, Download, ShieldCheck, Clock, ShoppingBag, XCircle, RefreshCw, Copy, QrCode, BadgeCheck, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notify } from '../../components/Notify';
import { getProfileApi, reenviarCarteirinhaApi } from '../../api';
import { montarUrlValidacao, currentServer } from '../../config';
import Brand from '../../components/Brand';
import FotoCropUpload from '../../components/FotoCropUpload';
import ComprovanteUpload from '../../components/ComprovanteUpload';
import './carteirinha.css';

function formatarData(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
}

// O motivo da reprovação vem como "motivo1; motivo2; detalhe" — quebra em lista.
function motivosLista(motivo) {
    if (!motivo) return [];
    return String(motivo).split(';').map((m) => m.trim()).filter(Boolean);
}

export default function MinhaCarteirinha() {
    const navigate = useNavigate();
    const [perfil, setPerfil] = useState(null);
    const [carregou, setCarregou] = useState(false);

    // PNG oficial (gerada pelo backend: fundo do ano + foto + dados + QR) —
    // usada só como PRÉVIA na tela. O download é o PDF (ver baixarPdf).
    const [pngUrl, setPngUrl] = useState(null);
    const [pngLoading, setPngLoading] = useState(false);
    const [pngErro, setPngErro] = useState(false);
    const [baixandoPdf, setBaixandoPdf] = useState(false);

    // Reenvio de carteirinha reprovada (foto/comprovante corrigidos).
    const [novaFoto, setNovaFoto] = useState('');
    const [novoComprovante, setNovoComprovante] = useState('');
    const [reenviando, setReenviando] = useState(false);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const res = await getProfileApi();
                if (vivo) setPerfil(res?.data || null);
            } catch {
                if (vivo) setPerfil(null);
            } finally {
                if (vivo) setCarregou(true);
            }
        })();
        return () => { vivo = false; };
    }, []);

    // Quando a carteirinha está APROVADA, busca a PNG renderizada no servidor.
    useEffect(() => {
        const c = perfil?.carteirinha;
        const reprovada = !!perfil?.pendencias?.detalhes?.digital_reprovada;
        const aprovada = !reprovada && c?.tem_valida && c?.status_aprovacao !== 'pendente';
        const cpf = String(c?.cpf || '').replace(/\D/g, '');
        if (!aprovada || !cpf) return undefined;

        let vivo = true;
        let urlCriada = null;
        setPngLoading(true); setPngErro(false); setPngUrl(null);
        (async () => {
            try {
                const res = await fetch(`${currentServer}/api/generatePngEstudante`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cpf, ano: c.ano, carteirinhaId: c.id }),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                urlCriada = URL.createObjectURL(blob);
                if (vivo) setPngUrl(urlCriada);
            } catch {
                if (vivo) setPngErro(true);
            } finally {
                if (vivo) setPngLoading(false);
            }
        })();
        return () => { vivo = false; if (urlCriada) URL.revokeObjectURL(urlCriada); };
    }, [perfil]);

    // Enquanto a carteirinha está PENDENTE, verifica sozinho, de tempos em tempos,
    // se o pagamento foi aprovado (ou reprovado) — sem o aluno precisar recarregar.
    // Só liga quando pendente; ao sair de pendente, o efeito re-roda e limpa o timer.
    const cartAtual = perfil?.carteirinha;
    const estaPendente = !!cartAtual?.tem_valida
        && cartAtual?.status_aprovacao === 'pendente'
        && !perfil?.pendencias?.detalhes?.digital_reprovada;

    useEffect(() => {
        if (!estaPendente) return undefined;
        let vivo = true;
        const id = setInterval(async () => {
            try {
                const res = await getProfileApi();
                if (!vivo) return;
                const novo = res?.data || null;
                const nc = novo?.carteirinha;
                const aindaPendente = !!nc?.tem_valida
                    && nc?.status_aprovacao === 'pendente'
                    && !novo?.pendencias?.detalhes?.digital_reprovada;
                setPerfil(novo);
                // Avisa na transição de pendente -> resolvido (a UI já troca sozinha).
                if (!aindaPendente) {
                    if (novo?.pendencias?.detalhes?.digital_reprovada) {
                        notify('Sua carteirinha foi analisada. Confira o status.');
                    } else if (nc?.tem_valida) {
                        notify('Boa notícia! Sua carteirinha foi aprovada.');
                    }
                }
            } catch {
                /* falha de rede: ignora e tenta no próximo ciclo */
            }
        }, 15000); // 15s
        return () => { vivo = false; clearInterval(id); };
    }, [estaPendente]);

    // Baixa a carteirinha DIGITAL em PDF (gerado no backend, com o link/QR de
    // validação clicável). A imagem mostrada na tela é só a prévia (PNG).
    const baixarPdf = async () => {
        const c = perfil?.carteirinha;
        const cpf = String(c?.cpf || '').replace(/\D/g, '');
        if (!cpf) return notify('Não foi possível identificar sua carteirinha.');
        setBaixandoPdf(true);
        let url = null;
        try {
            const res = await fetch(`${currentServer}/api/generatePdfEstudante`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cpf, ano: c.ano, carteirinhaId: c.id }),
            });
            if (!res.ok) {
                let msg = 'Não foi possível gerar o PDF agora. Tente novamente.';
                try { const j = await res.json(); msg = j.error || j.message || msg; } catch { /* corpo não-JSON */ }
                throw new Error(msg);
            }
            const blob = await res.blob();
            url = URL.createObjectURL(blob);
            const nomeSeguro = String(c?.nome || 'ueb').trim().replace(/\s+/g, '_');
            const a = document.createElement('a');
            a.href = url;
            a.download = `carteirinha_${nomeSeguro}_${c?.ano || ''}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (e) {
            notify(e?.message || 'Não foi possível baixar o PDF agora.');
        } finally {
            if (url) URL.revokeObjectURL(url);
            setBaixandoPdf(false);
        }
    };

    const Header = (
        <header className="ueb-page-head">
            <span className="ueb-page-ico"><IdCard size={22} /></span>
            <div>
                <h1 className="ueb-page-title">Minha carteirinha</h1>
                <p className="ueb-page-sub">Sua carteira digital de estudante.</p>
            </div>
        </header>
    );

    if (!carregou) {
        return (
            <div className="ueb-page">
                {Header}
                <div className="pcard" style={{ padding: 40, textAlign: 'center' }}><span className="pspin" style={{ color: 'var(--p-accent)' }} /></div>
            </div>
        );
    }

    const cart = perfil?.carteirinha;
    const detalhes = perfil?.pendencias?.detalhes || {};
    const reprovada = !!detalhes.digital_reprovada;
    const motivos = motivosLista(detalhes.motivo_reprovacao);
    const dadosRep = detalhes.dados_atuais || {};

    // ---------- 1. REPROVADA ----------
    if (reprovada) {
        // Descobre PELO MOTIVO o que precisa corrigir. Se não der pra identificar,
        // oferece os dois (o aluno reenvia o que precisar).
        const textoMotivos = String(detalhes.motivo_reprovacao || '').toLowerCase();
        const pediuFoto = /foto|imagem/.test(textoMotivos);
        const pediuComprovante = /comprovante/.test(textoMotivos);
        const semPista = !pediuFoto && !pediuComprovante;
        const mostrarFoto = pediuFoto || semPista;
        const mostrarComprovante = pediuComprovante || semPista;

        const reenviar = async () => {
            if (pediuFoto && !novaFoto) return notify('Anexe a nova foto para reenviar.');
            if (pediuComprovante && !novoComprovante) return notify('Anexe o novo comprovante para reenviar.');
            if (semPista && !novaFoto && !novoComprovante) return notify('Anexe a nova foto ou o comprovante para reenviar.');
            setReenviando(true);
            try {
                await reenviarCarteirinhaApi({ foto: novaFoto || undefined, comprovante: novoComprovante || undefined });
                notify('Enviado! Sua carteirinha voltou para análise.');
                setNovaFoto(''); setNovoComprovante('');
                const res = await getProfileApi();
                setPerfil(res?.data || null);
            } catch (err) {
                notify(err?.response?.data?.error || 'Não foi possível reenviar agora.');
            } finally {
                setReenviando(false);
            }
        };

        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-status ueb-status--rep pcard">
                    <span className="ueb-status-ico"><XCircle size={30} /></span>
                    <h2 className="ueb-status-title">Sua carteirinha foi reprovada</h2>
                    <p className="ueb-status-sub">
                        Nossa equipe revisou seus dados e encontrou algo que precisa ser corrigido{dadosRep.ano ? ` na carteira ${dadosRep.ano}` : ''}.
                        Corrija abaixo e reenvie — sua carteirinha volta para análise na hora.
                    </p>

                    {motivos.length > 0 && (
                        <div className="ueb-status-box">
                            <strong>Motivo{motivos.length > 1 ? 's' : ''}:</strong>
                            <ul className="ueb-status-motivos">
                                {motivos.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        </div>
                    )}

                    {(dadosRep.curso || dadosRep.instituicao) && (
                        <div className="ueb-status-dados">
                            {dadosRep.curso && <span><em>Curso</em> {dadosRep.curso}</span>}
                            {dadosRep.instituicao && <span><em>Instituição</em> {dadosRep.instituicao}</span>}
                        </div>
                    )}

                    <div className="ueb-reenvio">
                        {mostrarFoto && (
                            <div className="ueb-reenvio-item">
                                <span className="ueb-reenvio-label">Nova foto</span>
                                <FotoCropUpload value={novaFoto} onChange={setNovaFoto} onErro={notify} />
                            </div>
                        )}
                        {mostrarComprovante && (
                            <div className="ueb-reenvio-item">
                                <span className="ueb-reenvio-label">Novo comprovante de matrícula</span>
                                <ComprovanteUpload valor={novoComprovante} onChange={setNovoComprovante} onErro={notify} />
                            </div>
                        )}
                    </div>

                    <div className="ueb-reenvio-acoes">
                        <button className="pbtn pbtn--primary" onClick={reenviar} disabled={reenviando}>
                            {reenviando ? <span className="pspin" /> : <><Send size={16} /> Reenviar para análise</>}
                        </button>
                        <button className="pbtn pbtn--soft" onClick={() => window.location.reload()}>
                            <RefreshCw size={16} /> Atualizar status
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ---------- 2. SEM CARTEIRINHA DO ANO VIGENTE ----------
    if (!cart || !cart.tem_valida) {
        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-status pcard">
                    <span className="ueb-status-ico"><IdCard size={28} /></span>
                    <h2 className="ueb-status-title">Você ainda não tem carteirinha do ano vigente</h2>
                    <p className="ueb-status-sub">Emita a sua em “Produtos”. Assim que o pagamento for aprovado, ela aparece aqui.</p>
                    <button className="pbtn pbtn--primary" onClick={() => navigate('/produtos')}>
                        <ShoppingBag size={16} /> Ver produtos
                    </button>
                </div>
            </div>
        );
    }

    // ---------- 3. EM ANÁLISE (pendente) ----------
    if (cart.status_aprovacao === 'pendente') {
        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-status ueb-status--pend pcard">
                    <span className="ueb-status-ico"><Clock size={30} /></span>
                    <h2 className="ueb-status-title">Sua carteirinha está em análise</h2>
                    <p className="ueb-status-sub">
                        Recebemos seu pagamento e seus dados. Nossa equipe está conferindo a foto e o comprovante.
                        Assim que for aprovada, sua carteira digital fica disponível aqui.
                    </p>
                    <div className="ueb-status-dados">
                        {cart.curso && <span><em>Curso</em> {cart.curso}</span>}
                        {cart.instituicao && <span><em>Instituição</em> {cart.instituicao}</span>}
                        {cart.ano && <span><em>Ano</em> {cart.ano}</span>}
                    </div>
                    <p className="phint" style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="pspin" style={{ color: 'var(--p-accent)', width: 14, height: 14 }} />
                        Verificamos automaticamente enquanto você espera — não precisa recarregar.
                    </p>
                    <button className="pbtn pbtn--soft" onClick={() => window.location.reload()}>
                        <RefreshCw size={16} /> Atualizar agora
                    </button>
                </div>
            </div>
        );
    }

    // ---------- 4. APROVADA — painel completo ----------
    const validacaoUrl = montarUrlValidacao(cart.cpf, cart.codigo_uso);
    const copiarValidacao = () => {
        navigator.clipboard?.writeText(validacaoUrl)
            .then(() => notify('Link de validação copiado!'))
            .catch(() => notify('Não foi possível copiar.'));
    };

    return (
        <div className="ueb-page">
            {Header}

            <div className="ueb-cart-panel">
                {/* Coluna esquerda: a PNG oficial + baixar */}
                <div className="ueb-cart-png-col">
                    {pngLoading ? (
                        <div className="ueb-cart-png-box"><span className="pspin" style={{ color: 'var(--p-accent)' }} /></div>
                    ) : pngUrl ? (
                        <img src={pngUrl} alt="Sua carteirinha de estudante" className="ueb-cart-png" />
                    ) : (
                        <div className="ueb-cart">
                            <div className="ueb-cart-top"><Brand onLight /><span className="ueb-cart-ano">{cart.ano}</span></div>
                            <div className="ueb-cart-body">
                                <div className="ueb-cart-foto" aria-hidden>{String(cart.nome || '?').charAt(0)}</div>
                                <div className="ueb-cart-dados">
                                    <span className="ueb-cart-label">Nome</span>
                                    <strong className="ueb-cart-nome">{cart.nome || '—'}</strong>
                                    <div className="ueb-cart-grid">
                                        <div><span className="ueb-cart-label">Curso</span><p>{cart.curso || '—'}</p></div>
                                        <div><span className="ueb-cart-label">Instituição</span><p>{cart.instituicao || '—'}</p></div>
                                    </div>
                                </div>
                                <div className="ueb-cart-qr"><QRCode value={validacaoUrl} size={92} bgColor="transparent" fgColor="#ffffff" /><span>Validação</span></div>
                            </div>
                            <div className="ueb-cart-foot"><ShieldCheck size={14} /> Válido em todo o território nacional</div>
                        </div>
                    )}

                    {pngErro && !pngLoading && (
                        <p className="phint ueb-cart-warn">Não foi possível gerar o card oficial (a carteirinha pode estar sem foto). Fale com o atendimento.</p>
                    )}
                    <button className="pbtn pbtn--primary pbtn--block" onClick={baixarPdf} disabled={baixandoPdf}>
                        {baixandoPdf ? <span className="pspin" /> : <><Download size={18} /> Baixar carteirinha (PDF)</>}
                    </button>
                </div>

                {/* Coluna direita: informações + validação */}
                <aside className="ueb-cart-info">
                    <div className="ueb-cart-badge">
                        <BadgeCheck size={17} />
                        <span>Carteira ativa</span>
                        <b>válida até {formatarData(cart.validade_carteirinha)}</b>
                    </div>

                    <div className="pcard ueb-cart-datacard">
                        <h3 className="ueb-cart-h3">Dados do estudante</h3>
                        <div className="ueb-cart-fields">
                            <Campo label="Nome" valor={cart.nome} full />
                            <Campo label="CPF" valor={cart.cpf} />
                            <Campo label="Ano" valor={cart.ano} />
                            <Campo label="Curso" valor={cart.curso} full />
                            <Campo label="Instituição" valor={cart.instituicao} full />
                            <Campo label="Nível de ensino" valor={cart.escolaridade} />
                            <Campo label="Código de uso" valor={cart.codigo_uso} />
                        </div>
                    </div>

                    <div className="pcard ueb-cart-valida">
                        <div className="ueb-cart-valida-qr">
                            <QRCode value={validacaoUrl} size={104} bgColor="#ffffff" fgColor="#0f172a" />
                        </div>
                        <div className="ueb-cart-valida-txt">
                            <strong><QrCode size={15} /> Validação online</strong>
                            <p>Aponte a câmera no QR Code ou use o link para verificar a autenticidade da carteira.</p>
                            <button className="pbtn pbtn--soft pbtn--sm" onClick={copiarValidacao}><Copy size={14} /> Copiar link</button>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Campo({ label, valor, full }) {
    return (
        <div className={`ueb-cart-field${full ? ' ueb-cart-field--full' : ''}`}>
            <span className="ueb-cart-field-label">{label}</span>
            <span className="ueb-cart-field-value">{valor || '—'}</span>
        </div>
    );
}
